import { useEffect, useState, useRef } from 'react';
import Input from 'components/common/inputs/Input';
import ShadowCard from 'components/common/cards/ShadowCard';
import { Stack, Alert, Grid, Typography } from '@newulife/common';
import MarketsList from 'constants/markets';
import useContainerOnBlurAndFocus from 'hooks/useContainerOnBlurAndFocus';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { inputState, devToolsState } from 'state';
import { find } from 'lodash';

function IdVerificationSection(props) {
  const markets = new MarketsList();
  const { config, data = {}, isValid, inputs: dictionary, name } = props;
  const market = markets.find(config?.enrollment?.form?.sections?.[name]?.market).country;
  const fields = config?.enrollment?.form?.sections?.[name]?.fields || [];
  const configValidationFunction = config?.enrollment?.form?.sections?.[name]?.onBlur || '';
  const { title } = data;
  const [nameIsValid, set_nameIsValid] = useState(false);
  const [idIsValid, set_idIsValid] = useState(false);
  const [sectionIsValidating, set_sectionIsValidating] = useState(false);
  const [idStatus, set_idStatus] = useState(null);
  const inputs = useRecoilValue(inputState);
  const setInputs = useSetRecoilState(inputState);
  const [devToolsSettings, setDevToolsSettings] = useRecoilState(devToolsState);

  // Track last successfully validated values to skip revalidation when user blurs without changing
  const lastValidatedRef = useRef({ name: null, id: null });

  const onBlurFunction = async () => {
    // Only proceed if both inputs are valid (not in errored state)
    if (!nameIsValid || !idIsValid) {
      isValid(false);
      return;
    }

    // Get current input values (normalize for comparison)
    const currentName = inputs[fields[0]] != null ? String(inputs[fields[0]]).trim() : '';
    const currentId = inputs[fields[1]] != null ? String(inputs[fields[1]]).trim() : '';

    // Skip revalidation if values haven't changed (user blurred without editing)
    const lastValidated = lastValidatedRef.current;
    if (lastValidated.name === currentName && lastValidated.id === currentId) {
      return;
    }

    set_sectionIsValidating(true);
    const isGovIdValid = await configValidationFunction({
      governmentIDName: currentName,
      governmentID: currentId,
      country: market.toUpperCase(),
      devToolsSettings,
    });

    // Only store values when validation succeeds - enables skip-on-blur-without-change
    if (isGovIdValid === 'Valid') {
      lastValidatedRef.current = { name: currentName, id: currentId };
    }

    setInputs((prev) => ({
      ...prev,
      miscFields: {
        ...(prev.miscFields || {}),
        IsNameAndIDValidated: isGovIdValid === 'Valid',
      },
    }));
    set_idStatus(isGovIdValid);
    set_sectionIsValidating(false);
  };

  useEffect(() => {
    isValid(nameIsValid && idIsValid && idStatus === 'Valid');
  }, [idStatus, nameIsValid, idIsValid]);

  const [setRef, isFocused] = useContainerOnBlurAndFocus(onBlurFunction);

  const showAlert =
    ['Invalid', 'Blocked', 'Valid', 'Duplicate'].includes(idStatus) &&
    !sectionIsValidating &&
    !isFocused &&
    nameIsValid &&
    idIsValid;

  if (!fields?.length) return null;

  return (
    <>
      <ShadowCard title={title}>
        <Stack className="form-max" ref={setRef} gap={2} tabIndex="0">
          <Input
            id={fields[0]}
            key={`${name}-${fields[0]}`}
            dictionary={dictionary}
            data={data}
            country={config.market}
            market={markets.find(config?.market).country}
            dataTestId={`input-${fields[0]}`}
            isValid={set_nameIsValid}
          />
          <Input
            id={fields[1]}
            key={`${name}-${fields[1]}`}
            dictionary={dictionary}
            data={data}
            country={config.market}
            market={markets.find(config?.market).country}
            dataTestId={`input-${fields[1]}`}
            isValid={set_idIsValid}
          />
          {showAlert && (
            <Alert severity={idStatus !== 'Valid' ? 'error' : 'success'} variant="standard">
              <Grid gap={0.5} direction="column">
                <Typography align="left" variant="body2">
                  {idStatus === 'Duplicate'
                    ? find(dictionary, { id: 'validation-govId' })?.text
                    : idStatus === 'Invalid'
                      ? find(dictionary, { id: 'cn-id-invalid' })?.text
                      : find(dictionary, { id: 'identity-verified' })?.text}
                </Typography>
              </Grid>
            </Alert>
          )}
        </Stack>
        {sectionIsValidating && (
          <div className={`loader-ring fadeIn`}>
            <div></div>
          </div>
        )}
      </ShadowCard>
    </>
  );
}
export default IdVerificationSection;
